/**
 * Главный экран платной части.
 *
 * Не набор графиков, а список того, что можно сделать. Заголовок экрана —
 * сколько возможностей нашлось, и каждая карточка заканчивается действием,
 * начинающимся с глагола. Числа внутри посчитаны кодом; модель только
 * пересказала их словами, и это подписано.
 *
 * Карточка устроена так: УТВЕРЖДЕНИЕ → контекст → ЧТО СДЕЛАТЬ → чем это
 * подтверждается → чему верить нельзя. Порядок не случайный: человек читает
 * до первой понятной строки, и ею должна быть та, ради которой он пришёл.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Insight } from "../lib/api";
import { useSession } from "../session";
import {
  Badge,
  Card,
  ConfidenceBadge,
  Empty,
  ErrorBox,
  FilterBar,
  FilterGroup,
  LoadingDashboard,
  PageHeader,
  Paywall,
  SegmentedControl,
  Why,
} from "../components/ui";
import { FactList } from "./Health";
import { IconRefresh, IconSpark } from "../components/icons";
import { AREA_TITLE, STATEMENT_TITLE, num, plural, rangeLabel } from "../lib/format";

interface InsightsResponse {
  insights: Insight[];
  count: number;
  headline: string;
  feedback_actions: Record<string, string>;
}

const STATUSES = [
  { value: "new", label: "Новые" },
  { value: "testing", label: "В проверке" },
  { value: "done", label: "Сделано" },
  { value: "dismissed", label: "Отклонено" },
  { value: "all", label: "Все" },
];

export default function OpportunitiesPage() {
  const { channelId, can } = useSession();
  const [status, setStatus] = useState("new");
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["insights", channelId, status],
    queryFn: () => api.get<InsightsResponse>(`/insights/${channelId}?status=${status}&weeks=6`),
    enabled: Boolean(channelId),
  });

  const feedback = useMutation({
    mutationFn: ({ id, action }: { id: number; action: string }) =>
      api.post(`/insights/${channelId}/${id}/feedback`, { action }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["insights", channelId] }),
  });

  const rebuild = useMutation({
    mutationFn: () => api.post(`/jobs/run/weekly?channel_id=${channelId}`),
  });

  if (!channelId) {
    return (
      <Card>
        <Empty title="Сначала подключите канал" actions={<Link to="/channels"><button className="primary">Каналы</button></Link>} />
      </Card>
    );
  }
  if (query.error instanceof ApiError && query.error.isPaywall) {
    return <Paywall what="Поиск возможностей роста" />;
  }
  if (query.isLoading) return <LoadingDashboard />;
  if (query.error) return <ErrorBox error={query.error} />;
  const data = query.data!;

  const byArea = new Map<string, Insight[]>();
  for (const insight of data.insights) {
    byArea.set(insight.area, [...(byArea.get(insight.area) ?? []), insight]);
  }

  return (
    <>
      <PageHeader
        eyebrow="Детальная аналитика"
        title={data.headline}
        lede="Числа посчитаны кодом по данным вашего канала. Модель их только пересказывает и не имеет права добавить ни одной цифры."
        actions={
          can("run_jobs") && (
            <button onClick={() => rebuild.mutate()} disabled={rebuild.isPending}>
              <IconRefresh size={15} />
              Пересобрать
            </button>
          )
        }
      />

      <FilterBar>
        <FilterGroup label="Состояние">
          <SegmentedControl value={status} onChange={setStatus} options={STATUSES} ariaLabel="Состояние вывода" />
        </FilterGroup>
        <span className="spacer" />
        <span className="faint">
          {num(data.insights.length)} {plural(data.insights.length, "вывод", "вывода", "выводов")} за шесть недель
        </span>
      </FilterBar>

      {rebuild.isSuccess && (
        <p className="faint">
          Пересборка запущена в фоне: она заново читает конкурентов, закупы и содержание. Это может
          занять минуту.
        </p>
      )}

      {data.insights.length === 0 ? (
        <Card>
          <Empty title="Возможностей за период не нашлось" icon={<IconSpark size={22} />}>
            Это не поломка. Либо канал идёт ровно, либо данных пока мало: разбор содержания требует
            хотя бы восьми сопоставимых публикаций в каждой группе.
          </Empty>
        </Card>
      ) : (
        [...byArea.entries()].map(([area, items]) => (
          <Card
            key={area}
            title={AREA_TITLE[area] ?? area}
            note={`${items.length} ${plural(items.length, "вывод", "вывода", "выводов")}`}
          >
            <div>
              {items.map((insight) => (
                <InsightCard
                  key={insight.id}
                  insight={insight}
                  actions={data.feedback_actions}
                  onFeedback={(action) => feedback.mutate({ id: insight.id, action })}
                  busy={feedback.isPending}
                />
              ))}
            </div>
          </Card>
        ))
      )}
    </>
  );
}

function InsightCard({
  insight,
  actions,
  onFeedback,
  busy,
}: {
  insight: Insight;
  actions: Record<string, string>;
  onFeedback: (action: string) => void;
  busy: boolean;
}) {
  const { channelId } = useSession();
  const detail = useQuery({
    queryKey: ["insight", channelId, insight.id],
    queryFn: () => api.get<{ insight: Insight }>(`/insights/${channelId}/${insight.id}`),
    enabled: false,
  });

  return (
    <article className="insight">
      <div className="title">{insight.title}</div>
      <div className="body">{insight.body_fact}</div>
      {insight.body_context && <div className="body">{insight.body_context}</div>}

      <div className="action-line">{insight.action_text}</div>

      <div className="meta">
        <Badge tone={insight.statement_type === "fact" ? "accent" : "muted"}>
          {STATEMENT_TITLE[insight.statement_type]}
        </Badge>
        <ConfidenceBadge level={insight.confidence} />
        {insight.sample_size > 0 && <Badge tone="muted">выборка: {num(insight.sample_size)}</Badge>}
        <span className="faint">{rangeLabel(insight.period.from, insight.period.to)}</span>
        {insight.narrated_by && (
          <span className="faint" title="Текст пересказан моделью; числа посчитаны кодом">
            пересказ: {insight.narrated_by}
          </span>
        )}
      </div>

      {insight.body_limitations && (
        <div className="body">
          <strong>Чему здесь верить нельзя:</strong> {insight.body_limitations}
        </div>
      )}

      <Why summary="Из чего это посчитано">
        <button className="ghost small" onClick={() => detail.refetch()} disabled={detail.isFetching}>
          {detail.data ? "Обновить" : "Показать числа"}
        </button>
        {detail.data && (
          <div style={{ marginTop: 12 }}>
            <FactList facts={detail.data.insight.facts ?? {}} />
          </div>
        )}
      </Why>

      {insight.status === "new" && (
        <div className="feedback">
          {/* Главная кнопка — «проверить», и только когда под вывод
              действительно можно завести эксперимент. Делать главной
              «Сделано» значит подталкивать закрыть вывод, не проверив его. */}
          {Object.entries(actions).map(([key, label]) => (
            <button
              key={key}
              className={key === "test_it" && insight.has_experiment ? "primary small" : "small"}
              disabled={busy}
              onClick={() => onFeedback(key)}
            >
              {label}
            </button>
          ))}
          {insight.has_experiment && (
            <span className="faint" style={{ alignSelf: "center" }}>
              «Проверить гипотезу» заведёт эксперимент
            </span>
          )}
        </div>
      )}
      {insight.status === "testing" && (
        <p className="faint" style={{ marginTop: 12 }}>
          Отправлено в <Link to="/experiments">эксперименты</Link>.
        </p>
      )}
    </article>
  );
}
